import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { emailSchema, signInSchema } from '@cercle/shared';
import { useApp } from '@/shared/app-context';
import { errorMessage } from '@/shared/http/http-client';
import { Button, ErrorText, Field, Input, Kicker, Muted } from '@/shared/ui';
import { PasswordInput } from './PasswordInput';
import styles from './auth.module.scss';

/**
 * Écran 00 — connexion.
 *
 * Email et mot de passe pour la voie normale ; le lien de connexion par email reste
 * disponible en secours, et sert aussi de « mot de passe oublié » — c'est par lui qu'un
 * membre en redéfinit un, sans qu'il faille un second circuit de réinitialisation.
 */
export function LoginScreen(): ReactNode {
  const { api, setMember } = useApp();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkSent, setLinkSent] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const parsed = signInSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Identifiants invalides.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const member = await api.auth.signIn(parsed.data.email, parsed.data.password);
      setMember(member);
      void navigate('/', { replace: true });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  /** Envoie un lien de connexion — secours et réinitialisation de mot de passe. */
  async function sendLink(): Promise<void> {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError('Saisissez d’abord votre email pour recevoir un lien.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.auth.requestMagicLink(parsed.data);
      setLinkSent(true);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.gate}>
      <div className={styles.gateInner}>
        <header className={styles.gateBrand}>
          <h1>Cercle des Anges</h1>
          <Kicker>In Fusto Veritas</Kicker>
        </header>

        <p className={styles.lede}>
          Le cercle se retrouve le 1<sup>er</sup> mardi du mois, salle Courbet. La part des anges
          vous attend.
        </p>

        <form className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
          <Field label="Votre email">
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="camille@cercle.fr"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </Field>

          <Field label="Mot de passe">
            <PasswordInput
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              required
            />
          </Field>

          <ErrorText>{error}</ErrorText>

          <Button type="submit" block disabled={busy}>
            {busy ? 'Connexion…' : 'Entrer dans le cercle'}
          </Button>
        </form>

        <div className={styles.recovery}>
          {linkSent ? (
            <p role="status">
              <Muted>
                Si cette adresse appartient au cercle, un lien de connexion vient d’y être
                envoyé. Il vous permettra d’entrer et de choisir un nouveau mot de passe.
              </Muted>
            </p>
          ) : (
            <Button variant="ghost" disabled={busy} onClick={() => void sendLink()}>
              Mot de passe oublié ? Recevoir un lien
            </Button>
          )}
        </div>

        <p className={styles.gateFoot}>
          <Muted>Cercle fermé · aucune inscription publique.</Muted>
          <br />
          <Link to="/invite">J’ai un code d’invitation</Link>
        </p>
      </div>
    </div>
  );
}
